import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { API_URL } from "../globals";
import { supabase } from "../supabaseClient";
import axios from "axios";

const NotificationsTab = () => {
  const [notificationWhen, setNotificationWhen] = useState('always');
  const [failureRate, setFailureRate] = useState(5);
  const [slackChannel, setSlackChannel] = useState('');
  const [customRules, setCustomRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [teamId, setTeamId] = useState(null);
  const [notificationSettingId, setNotificationSettingId] = useState(null);

  // Fetch user and team data to get team ID
  const fetchTeamData = async () => {
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/user`, { headers });
      if (response.data?.team?.id) {
        setTeamId(response.data.team.id);
        return response.data.team.id;
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      showSnackbar('Error fetching team data', 'error');
    }
    return null;
  };

  // Fetch notification settings
  const fetchNotificationSettings = async (teamId) => {
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/teams/${teamId}/notification_settings`, { headers });
      
      if (response.data?.notification_setting) {
        const settings = response.data.notification_setting;
        setNotificationSettingId(settings.id);
        setNotificationWhen(settings.notification_when || 'always');
        setFailureRate(settings.failure_rate_threshold || 5);
        setSlackChannel(settings.slack_channel || '');
        setCustomRules(settings.notification_rules?.map(rule => ({
          id: rule.id,
          threshold: rule.threshold,
          actionType: rule.action_type,
          value: rule.value
        })) || []);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching notification settings:', error);
        showSnackbar('Error fetching notification settings', 'error');
      }
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      const teamId = await fetchTeamData();
      if (teamId) {
        await fetchNotificationSettings(teamId);
      }
      setIsLoading(false);
    };

    initializeData();
  }, []);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleNotificationWhenChange = (event) => {
    setNotificationWhen(event.target.value);
  };

  const handleFailureRateChange = (event) => {
    const value = parseInt(event.target.value) || 0;
    setFailureRate(Math.max(0, Math.min(100, value))); // Clamp between 0-100
  };

  const handleSlackChannelChange = (event) => {
    setSlackChannel(event.target.value);
  };

  const addNewRule = () => {
    setCustomRules([
      ...customRules,
      {
        id: `temp_${Date.now()}`, // Temporary ID for new rules
        threshold: 50,
        actionType: 'tag',
        value: '',
      }
    ]);
  };

  const updateRule = (id, field, value) => {
    setCustomRules(customRules.map(rule => {
      if (rule.id === id) {
        const updatedRule = { ...rule, [field]: value };
        // If changing to tag_channel type and value is not in correct format, initialize it
        if (field === 'actionType' && value === 'tag_channel' && !rule.value.includes('|')) {
          updatedRule.value = '|'; // Initialize with separator
        }
        // If changing from tag_channel to other types, clean up the value
        if (field === 'actionType' && rule.actionType === 'tag_channel' && value !== 'tag_channel') {
          updatedRule.value = ''; // Reset value
        }
        // If changing to tag_subteam from tag_channel, clean up the value
        if (field === 'actionType' && rule.actionType === 'tag_channel' && value === 'tag_subteam') {
          updatedRule.value = ''; // Reset value from channel format
        }
        return updatedRule;
      }
      return rule;
    }));
  };

  const deleteRule = (id) => {
    setCustomRules(customRules.filter(rule => rule.id !== id));
  };

  const saveSettings = async () => {
    if (!teamId) {
      showSnackbar('Team data not available', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const requestData = {
        notification_when: notificationWhen,
        failure_rate_threshold: failureRate,
        slack_channel: slackChannel,
        rules: customRules.map(rule => ({
          threshold: rule.threshold,
          action_type: rule.actionType,
          value: rule.value
        }))
      };

      const url = `${API_URL}/teams/${teamId}/notification_settings`;
      const method = notificationSettingId ? 'PUT' : 'POST';
      
      const response = await axios({
        method,
        url,
        data: requestData,
        headers
      });

      if (response.data?.notification_setting) {
        const settings = response.data.notification_setting;
        setNotificationSettingId(settings.id);
        
        // Update custom rules with real IDs from the server
        if (settings.notification_rules) {
          setCustomRules(settings.notification_rules.map(rule => ({
            id: rule.id,
            threshold: rule.threshold,
            actionType: rule.action_type,
            value: rule.value
          })));
        }
      }

      showSnackbar('Notification settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showSnackbar('Error saving notification settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress size={32} sx={{ color: '#fff' }} />
      </Box>
    );
  }

  return (
    <>
      {/* Notifications Tab Content */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 6
      }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600, 
              color: '#fff',
              mb: 1,
              fontSize: '1.75rem',
            }}
          >
            Notification Settings
          </Typography>
          <Typography sx={{ 
            color: '#888', 
            fontSize: '0.875rem',
            maxWidth: '460px',
            lineHeight: 1.6,
          }}>
            Configure Slack notifications for your test results.
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '8px',
        p: 4,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Slack Notifications Section */}
          <Box>
            <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 3 }}>
              SLACK NOTIFICATIONS
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem', mb: 2 }}>
                  Send Slack notifications for test results
                </Typography>
                
                <FormControl component="fieldset">
                  <RadioGroup
                    value={notificationWhen}
                    onChange={handleNotificationWhenChange}
                    sx={{ gap: 1 }}
                  >
                    <FormControlLabel 
                      value="always" 
                      control={
                        <Radio 
                          sx={{
                            color: '#666',
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                          }}
                        />
                      } 
                      label={
                        <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                          Always
                        </Typography>
                      }
                    />
                    
                    <FormControlLabel 
                      value="failure_rate" 
                      control={
                        <Radio 
                          sx={{
                            color: '#666',
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                          }}
                        />
                      } 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                            If failure rate is &gt;
                          </Typography>
                          <TextField
                            size="small"
                            value={failureRate}
                            onChange={handleFailureRateChange}
                            type="number"
                            disabled={notificationWhen !== 'failure_rate'}
                            sx={{
                              width: '80px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#2a2a2a',
                                color: '#fff !important',
                                height: '32px',
                                '& fieldset': {
                                  borderColor: '#f8f8f8 !important',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#f8f8f8 !important',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#f8f8f8 !important',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: '#1a1a1a',
                                  color: '#666 !important',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                color: '#fff !important',
                              },
                              '& .MuiOutlinedInput-input.Mui-disabled': {
                                color: '#666 !important',
                                WebkitTextFillColor: '#666 !important',
                              },
                              '& input': {
                                color: '#fff !important',
                              },
                              '& input:disabled': {
                                color: '#666 !important',
                                WebkitTextFillColor: '#666 !important',
                              },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                              },
                              '& input[type=number]': {
                                '-moz-appearance': 'textfield',
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography sx={{ color: '#fff !important' }}>%</Typography>
                                </InputAdornment>
                              ),
                              inputProps: { 
                                min: 0, 
                                max: 100,
                                style: {
                                  // Remove increment/decrement buttons
                                  '-moz-appearance': 'textfield',
                                  '&::-webkit-outer-spin-button': {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                  },
                                  '&::-webkit-inner-spin-button': {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                      }
                    />
                    
                    <FormControlLabel 
                      value="never" 
                      control={
                        <Radio 
                          sx={{
                            color: '#666',
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                          }}
                        />
                      } 
                      label={
                        <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                          Never
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Slack Channel Input - only show if not "never" */}
              {notificationWhen !== 'never' && (
                <Box>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem', mb: 1 }}>
                    Slack Channel
                  </Typography>
                  <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 2 }}>
                    Enter the channel name where notifications should be sent
                  </Typography>
                  <TextField
                    fullWidth
                    value={slackChannel}
                    onChange={handleSlackChannelChange}
                    placeholder="e.g., #test-results"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#2a2a2a',
                        color: '#fff',
                        '& fieldset': {
                          borderColor: '#444',
                        },
                        '&:hover fieldset': {
                          borderColor: '#666',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#888',
                        },
                        '& input::placeholder': {
                          color: '#666',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Custom Rules Section */}
              <Box sx={{ mt: 4 }}>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem', mb: 2 }}>
                  Custom Notification Rules
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 3 }}>
                  Set up custom rules to tag people or forward messages based on failure rates
                </Typography>

                {customRules.map((rule) => (
                  <Box
                    key={rule.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                      p: 2,
                      backgroundColor: '#2a2a2a',
                      borderRadius: '4px',
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                      When failure rate is &gt;
                    </Typography>
                    <TextField
                      size="small"
                      value={rule.threshold}
                      onChange={(e) => updateRule(rule.id, 'threshold', parseInt(e.target.value) || 0)}
                      type="number"
                      sx={{
                        width: '80px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          color: '#fff',
                          '& fieldset': {
                            borderColor: '#444',
                          },
                          '&:hover fieldset': {
                            borderColor: '#666',
                          },
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end"><Typography sx={{ color: '#fff' }}>%</Typography></InputAdornment>,
                        inputProps: { 
                          min: 0, 
                          max: 100,
                          style: {
                            '-moz-appearance': 'textfield',
                          }
                        }
                      }}
                    />
                    
                    <Select
                      size="small"
                      value={rule.actionType}
                      onChange={(e) => updateRule(rule.id, 'actionType', e.target.value)}
                      sx={{
                        width: '170px',
                        backgroundColor: '#1a1a1a',
                        color: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#444',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#666',
                        },
                        '& .MuiSelect-icon': {
                          color: '#fff',
                        },
                      }}
                    >
                      <MenuItem value="tag">Tag Someone</MenuItem>
                      <MenuItem value="forward">Forward to Channel</MenuItem>
                      <MenuItem value="tag_channel">Tag Channel/Group</MenuItem>
                      <MenuItem value="tag_subteam">Tag Subteam</MenuItem>
                    </Select>

{rule.actionType === 'tag_channel' ? (
                      // Two fields for channel ID and name
                      <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                        <TextField
                          size="small"
                          value={rule.value.split('|')[0] || ''}
                          onChange={(e) => {
                            const channelId = e.target.value.replace(/[@#]/g, '');
                            const channelName = rule.value.split('|')[1] || '';
                            updateRule(rule.id, 'value', `${channelId}|${channelName}`);
                          }}
                          placeholder="Channel ID"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#1a1a1a',
                              color: '#fff',
                              '& fieldset': {
                                borderColor: '#444',
                              },
                              '&:hover fieldset': {
                                borderColor: '#666',
                              },
                            },
                          }}
                        />
                        <TextField
                          size="small"
                          value={rule.value.split('|')[1] || ''}
                          onChange={(e) => {
                            const channelId = rule.value.split('|')[0] || '';
                            const channelName = e.target.value.replace(/[@#]/g, '');
                            updateRule(rule.id, 'value', `${channelId}|${channelName}`);
                          }}
                          placeholder="Channel Name"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#1a1a1a',
                              color: '#fff',
                              '& fieldset': {
                                borderColor: '#444',
                              },
                              '&:hover fieldset': {
                                borderColor: '#666',
                              },
                            },
                          }}
                        />
                      </Box>
                    ) : rule.actionType === 'tag_subteam' ? (
                      // Single field for subteam ID
                      <TextField
                        size="small"
                        value={rule.value}
                        onChange={(e) => {
                          // Remove @ or # if user tries to type them
                          const newValue = e.target.value.replace(/[@#]/g, '');
                          updateRule(rule.id, 'value', newValue);
                        }}
                        placeholder="subteam ID"
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#1a1a1a',
                            color: '#fff',
                            '& fieldset': {
                              borderColor: '#444',
                            },
                            '&:hover fieldset': {
                              borderColor: '#666',
                            },
                            '& .MuiInputAdornment-root': {
                              marginRight: '4px',
                              '& p': {
                                color: '#fff',
                                fontSize: '0.875rem',
                              },
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>!</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      // Single field for tag or forward
                      <TextField
                        size="small"
                        value={rule.value}
                        onChange={(e) => {
                          // Remove @ or # if user tries to type them
                          const newValue = e.target.value.replace(/[@#]/g, '');
                          updateRule(rule.id, 'value', newValue);
                        }}
                        placeholder={rule.actionType === 'tag' ? 'username' : 'channel'}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#1a1a1a',
                            color: '#fff',
                            '& fieldset': {
                              borderColor: '#444',
                            },
                            '&:hover fieldset': {
                              borderColor: '#666',
                            },
                            '& .MuiInputAdornment-root': {
                              marginRight: '4px',
                              '& p': {
                                color: '#fff',
                                fontSize: '0.875rem',
                              },
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography>{rule.actionType === 'tag' ? '@' : '#'}</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}

                    <IconButton
                      onClick={() => deleteRule(rule.id)}
                      sx={{
                        color: '#888',
                        '&:hover': {
                          color: '#ff4444',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}

                <Button
                  onClick={addNewRule}
                  variant="outlined"
                  sx={{
                    mt: 2,
                    color: '#fff',
                    borderColor: '#444',
                    '&:hover': {
                      borderColor: '#666',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Add Rule
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: '1px solid #2a2a2a' }}>
            <Button
              onClick={saveSettings}
              disabled={
                isSaving || 
                (notificationWhen !== 'never' && !slackChannel.trim()) ||
                customRules.some(rule => {
                  if (rule.actionType === 'tag_channel') {
                    const [channelId, channelName] = rule.value.split('|');
                    return !channelId?.trim() || !channelName?.trim();
                  }
                  return !rule.value.trim();
                })
              }
              variant="contained"
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                px: 4,
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
                '&:disabled': {
                  backgroundColor: '#666',
                  color: '#ccc',
                },
              }}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#000', mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            backgroundColor: snackbar.severity === 'success' ? '#4caf50' : snackbar.severity === 'error' ? '#f44336' : '#2196f3',
            color: '#fff',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationsTab; 